import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "@radix-ui/react-slot"

import { cn } from "../../../lib/utils"

const captionVariants = cva("font-sans", {
  variants: {
    size: {
      xs: "text-[10px]",
      sm: "text-xs",
      base: "text-sm",
    },
    variant: {
      default: "text-muted-foreground",
      accent: "text-primary",
      destructive: "text-destructive",
      success: "text-green-600 dark:text-green-500",
      warning: "text-yellow-600 dark:text-yellow-500",
    },
    weight: {
      normal: "font-normal",
      medium: "font-medium",
    },
  },
  defaultVariants: {
    size: "sm",
    variant: "default",
    weight: "normal",
  },
})

export interface CaptionProps
  extends React.HTMLAttributes<HTMLElement>,
    VariantProps<typeof captionVariants> {
  /**
   * Render as a different element using Radix Slot
   */
  asChild?: boolean
  /**
   * The HTML element to render as
   * @default "span"
   */
  as?: "span" | "small" | "p" | "div" | "figcaption" | "time"
}

/**
 * Caption component for metadata, helper text, timestamps, and small annotations.
 */
function Caption({
  className,
  size,
  variant,
  weight,
  asChild = false,
  as = "span",
  children,
  ...props
}: CaptionProps) {
  const Comp = asChild ? Slot : as

  return (
    <Comp
      data-slot="caption"
      data-size={size}
      data-variant={variant}
      className={cn(captionVariants({ size, variant, weight }), className)}
      {...props}
    >
      {children}
    </Comp>
  )
}

Caption.displayName = "Caption"

export { Caption }
